import {faTrash} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Checkbox, DatePicker, DatePickerProps, Form, Input, Radio, Row, Select, Switch} from 'antd';
import dayjs, {Dayjs} from 'dayjs';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {appRoute} from '../../../app-route';
import {AddButton} from '../../../component/add-button/add-button';
import {ALL_DAYS_ID} from '../../../const';
import {AdditionalInfo} from '../../../layout/additional-info/additional-info';
import {NavigationLink} from '../../../layout/navigation-link/navigation-link';
import {PageCard} from '../../../layout/page-card/page-card';
import {RatingMultiSelect} from '../../../layout/rating-multi-select/rating-multi-select';
import {selectDayOfWeekContent} from '../../../layout/select/select-helper';
import {TimeRangePicker} from '../../../layout/time-range-picker/time-range-picker';
import {useCatalogInfo, useCatalogsRules, useStoreRulesApps} from '../../../provider/catalogs/catalogs-hook';
import {CatalogRulesType} from '../../../provider/catalogs/catalogs-type';
import {useLicenses} from '../../../provider/license/license-hook';
import {Locale} from '../../../provider/locale/locale';
import {useLocale} from '../../../provider/locale/locale-hook';
import {useModal} from '../../../provider/modal/modal-hook';
import {useSnackbar} from '../../../provider/snackbar/snackbar-hook';
import {AnalyticsTarget, track} from '../../../service/analytics/analytics';
import {TimezoneType} from '../../../service/api/api-type';
import {useTimezoneLibrary} from '../../../service/api-hook/api-hook';
import {CompaniesSelectorFeatureEnum} from '../../../service/feature-companies/feature-companies-const';
import {useCompaniesSelector} from '../../../service/feature-companies/feature-companies-hook';
import {useFormRules} from '../../../service/form/form-rules-hook';
import {RatingType} from '../../../service/reviews/reviews-rating';
import {
    AutoReplyDataErrorType,
    AutoReplyErrorEnum,
    AutoReplyRuleTypeEnum,
    AutoReplyToReviewBodyType,
    AutoReplyToReviewItemType,
    AutoReplyTypeEnum,
    defaultAutoReplyInfoErrors,
    defaultAutoReplyToReviewData,
    fetchAutoReplayCreate,
    fetchAutoReplayDelete,
    fetchAutoReplayUpdate,
    KeywordOperatorEnum,
    KeywordRuleType,
    ReviewsAttachmentsFilterEnum,
} from '../../../service/reviews/reviews-response-templates';
import {useReviewsAspects} from '../../../service/reviews/reviews-tags';
import {FeaturesEnum} from '../../../service/user/user-type';
import {handleFilterOption} from '../../../util/antd/select-helper';
import {classNames} from '../../../util/css';
import {noop} from '../../../util/function';
import {getMapFromObject} from '../../../util/object';
import {IdNumberType} from '../../../util/type';
import {useUrl} from '../../../util/url-hook/url-hook';

import {aspectIdsToTitles, buildReviewConditionsPayload} from './auto-reply-form-conditions-helper';
import {
    AUTO_REPLY_DATE_FORMAT,
    AUTO_REPLY_MIN_PAST_OFFSET_DAYS,
    AutoReplyFormFieldNameEnum,
} from './auto-reply-form-const';
import {getAutoReplyFormApiErrorMessage} from './auto-reply-form-error';
import {AutoReplyFormFiltersCompany} from './auto-reply-form-filters-company';
import {AutoReplyFormFiltersStore} from './auto-reply-form-filters-store';
import {AutoReplyTemplatePopup} from './auto-reply-form-popup/auto-reply-template-popup';
import {AutoReplyFormReviewTargetSelector} from './auto-reply-form-review-target-selector';
import {getStoreSourcesFromApps} from './auto-reply-form-store-source-helper';
import * as styles from './auto-reply-form.scss';

const {TextArea} = Input;
const {Option} = Select;

type PropsType = {
    mode: 'edit' | 'create';
    className?: string;
    autoReplyData: AutoReplyToReviewItemType;
    autoReplyId?: string;
    ruleType?: AutoReplyRuleTypeEnum;
};

type ReviewsTimeBucketType = {
    start: string;
    end: string;
};

const minimumSelectedTemplatesForOrderButtonEnable = 2;

// Keyword conditions are limited to at most one "contains" and one "not_contains" rule.
const KEYWORD_OPERATORS = [KeywordOperatorEnum.contains, KeywordOperatorEnum.notContains];

// eslint-disable-next-line complexity, max-statements, sonarjs/cognitive-complexity
export function AutoReplyForm(props: PropsType): JSX.Element {
    const {className, mode, autoReplyData, autoReplyId, ruleType: initialRuleType} = props;

    const resolvedRuleType =
        initialRuleType ||
        autoReplyData.ruleType ||
        (autoReplyData.brandCatalogCompany ? AutoReplyRuleTypeEnum.Store : AutoReplyRuleTypeEnum.Company);

    const [isSelectTemplatePopupOpen, setIsSelectTemplatePopupOpen] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState(false);

    const [form] = Form.useForm();
    const isEdit = mode === 'edit';
    const isCreate = mode === 'create';
    const {getLocalizedString} = useLocale();
    const {modal} = useModal();
    const timezoneLibrary = useTimezoneLibrary();
    const timezoneList: Array<TimezoneType> = timezoneLibrary.result || [];
    const [temporaryDate, setTemporaryDate] = useState<boolean>(autoReplyData.mode === 'manual');

    const {getCompaniesMultiselectValidationRule, maxLengthFieldRule, minLengthFieldRule, requiredFieldRule} =
        useFormRules();
    const {licenses} = useLicenses();

    const isActiveGptAutoReplyLicence = licenses[FeaturesEnum.gptAutoReplyLicence]?.isActive;
    const isActiveAutoReplyLicence = licenses[FeaturesEnum.reviewSuggests]?.isActive;

    const [infiniteDate, setInfiniteDate] = useState<boolean>(isCreate);

    const {getCatalogName, isInProgress: catalogNamesIsInProgress} = useCatalogInfo();
    const {result: catalogsResult, isInProgress: catalogsIsInProgress} = useCatalogsRules({autoReplySupported: true});
    const {result: storeAppsResult, isInProgress: storeAppsIsInProgress} = useStoreRulesApps();
    const storeApps = useMemo(() => storeAppsResult || [], [storeAppsResult]);
    // The review sources are the distinct catalogs of these applications, so neither select can
    // offer a source without applications nor an application from another source.
    const storeCatalogs = useMemo(
        () => getStoreSourcesFromApps(storeApps, getCatalogName),
        [storeApps, getCatalogName]
    );
    const hasStoreCatalogs = storeCatalogs.length > 0;
    const isStoreOptionDisabled = !hasStoreCatalogs && resolvedRuleType !== AutoReplyRuleTypeEnum.Store;
    const [selectedTemplateList, setSelectedTemplateList] = useState<Array<IdNumberType>>(
        autoReplyData.responseTemplates || []
    );
    const [defaultSelectedTemplateList, setDefaultSelectedTemplateList] = useState<Array<IdNumberType>>(
        autoReplyData.responseTemplates || []
    );
    const [enabledForDates, setEnabledForDates] = useState<Array<string>>(autoReplyData.enabledForDates || []);
    const {pushUrl} = useUrl();
    const {snackbar} = useSnackbar();

    const isShowTemplateBlock = Form.useWatch(['replyType'], form) === AutoReplyTypeEnum.Template;
    const selectedRuleType: AutoReplyRuleTypeEnum =
        Form.useWatch(AutoReplyFormFieldNameEnum.RuleType, form) ?? resolvedRuleType;
    const isStoreRuleType = selectedRuleType === AutoReplyRuleTypeEnum.Store;
    const selectedCatalogIds: Array<number> = Form.useWatch('catalogs', form) ?? [];

    const keywordRulesValue: Array<KeywordRuleType | undefined> = Form.useWatch('reviewsKeywordRules', form) ?? [];
    const canAddKeywordRule = keywordRulesValue.length < KEYWORD_OPERATORS.length;
    const nextKeywordOperator =
        KEYWORD_OPERATORS.find(
            (operator) => !keywordRulesValue.some((keywordRule) => keywordRule?.operator === operator)
        ) ?? KeywordOperatorEnum.contains;

    const catalogsHookResult: Array<CatalogRulesType> = useMemo(
        (): Array<CatalogRulesType> => catalogsResult || [],
        [catalogsResult]
    );

    const aspectsQuery = useReviewsAspects(true);
    const aspectsList = useMemo(() => aspectsQuery.data ?? [], [aspectsQuery.data]);

    useEffect(() => {
        form.resetFields([
            'name',
            'description',
            'catalogs',
            AutoReplyFormFieldNameEnum.StoreCatalog,
            AutoReplyFormFieldNameEnum.BrandCatalogCompany,
            AutoReplyFormFieldNameEnum.RuleType,
            'reviewsRatings',
            'reviewsWithBody',
            'reviewsAttachmentsFilter',
            'reviewsKeywordRules',
            'reviewsAutoTagTitles',
            'datePeriod',
            'dateStart',
            'reviewsTimeBucket',
            'templateTimeZone',
            'templateSelectionStrategy',
            'is-test',
            'reviewsWeekdaysIndexes',
            'enabled',
        ]);
        form.setFieldsValue({[AutoReplyFormFieldNameEnum.RuleType]: resolvedRuleType});
    }, [autoReplyData, form, resolvedRuleType]);

    // Auto-tags are stored as flat tag ids but selected by aspect title; restore the selected
    // titles once the aspects list is loaded.
    useEffect(() => {
        if (aspectsList.length > 0) {
            form.setFieldsValue({
                reviewsAutoTagTitles: aspectIdsToTitles(autoReplyData.reviewsAutoTagIds, aspectsList),
            });
        }
    }, [aspectsList, autoReplyData, form]);

    useEffect(() => {
        if (isEdit) {
            setInfiniteDate(Boolean(autoReplyData.enabledFrom && !autoReplyData.enabledTo));
        }

        setSelectedTemplateList(autoReplyData.responseTemplates || []);
        setDefaultSelectedTemplateList(autoReplyData.responseTemplates || []);
        setTemporaryDate(autoReplyData.mode === 'manual');
        setEnabledForDates(autoReplyData.enabledForDates || []);
    }, [autoReplyData, isEdit]);

    const companiesSelector = useCompaniesSelector({
        feature: CompaniesSelectorFeatureEnum.ReviewSuggests,
        resourceId: autoReplyId ? String(autoReplyId) : null,
        initialCompaniesCount: autoReplyData.companies?.length || 0,
        raceUrl: true,
    });

    const companiesSelectorGpt = useCompaniesSelector({
        feature: CompaniesSelectorFeatureEnum.ReviewGptSuggests,
        resourceId: autoReplyId ? String(autoReplyId) : null,
        initialCompaniesCount: autoReplyData.companies?.length || 0,
        raceUrl: true,
    });

    const {selectedCount, selectorId, initializeSelector} = companiesSelector;
    const {
        selectedCount: selectedCountGpt,
        selectorId: selectorIdGpt,
        initializeSelector: initializeSelectorGpt,
    } = companiesSelectorGpt;

    useEffect(() => {
        initializeSelectorGpt();
        initializeSelector();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoReplyData.id]);

    useEffect(() => {
        form.setFieldsValue({companies: isShowTemplateBlock ? selectorId : selectorIdGpt});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectorId, isShowTemplateBlock, selectorIdGpt]);

    const handleDelete = useCallback(() => {
        modal.confirm({
            maskClosable: true,
            title: <Locale stringKey="AUTO_REPLIES__REMOVING_A_RULE" />,
            content: (
                <Locale
                    stringKey="AUTO_REPLIES__THE_RULE_RULE_NAME_WILL_BE_REMOVED_CONFIRM"
                    valueMap={{
                        ruleNames: autoReplyData.name,
                    }}
                />
            ),
            okText: <Locale stringKey="TEXT__APPLY" />,
            cancelText: <Locale stringKey="POPUP__BUTTON__CANCEL" />,
            cancelButtonProps: {type: 'primary'},
            okButtonProps: {type: 'default', danger: true},
            onOk: async () => {
                try {
                    await fetchAutoReplayDelete(autoReplyId ?? '', resolvedRuleType);
                    snackbar.success(<Locale stringKey="AUTO_REPLIES__NOTIFICATION__SUCCESS_DELETE" />);
                    pushUrl(appRoute.autoReplies.path);
                } catch (error) {
                    const message = getAutoReplyFormApiErrorMessage(error);

                    snackbar.error(message ?? <Locale stringKey="AUTO_REPLIES__NOTIFICATION__ERROR_DELETE" />);
                }
            },
        });
    }, [modal, autoReplyData.name, autoReplyId, resolvedRuleType, snackbar, pushUrl]);

    const getReviewsTimeBucket = useCallback((): ReviewsTimeBucketType | null => {
        const checkTime = form.getFieldValue('reviewsTimeBucket') ? form.getFieldValue('reviewsTimeBucket')[0] : null;

        if (checkTime) {
            return {
                start: dayjs(checkTime).format('HH:mm'),
                end: dayjs(form.getFieldValue('reviewsTimeBucket')[1]).format('HH:mm'),
            };
        }

        return null;
    }, [form]);

    const [errors, setErrors] = useState<AutoReplyDataErrorType>(defaultAutoReplyInfoErrors);

    const validateFields = useCallback((): boolean => {
        const datePeriod = form.getFieldValue('datePeriod');
        const dateStart = form.getFieldValue('dateStart');

        const enabledFrom = datePeriod && form.getFieldValue('datePeriod')[0];
        const enabledTo = datePeriod && form.getFieldValue('datePeriod')[1];

        const newErrors: AutoReplyDataErrorType = {
            selectTemplates: isShowTemplateBlock ? selectedTemplateList.length === 0 : false,
            datePeriod: !temporaryDate && !infiniteDate && !(enabledFrom && enabledTo),
            dateStart: !temporaryDate && infiniteDate && !dateStart,
        };

        setErrors(newErrors);

        return Object.values(newErrors).some(Boolean);
    }, [form, isShowTemplateBlock, selectedTemplateList.length, temporaryDate, infiniteDate]);

    function updateErrors(key: AutoReplyErrorEnum, value: boolean): void {
        setErrors({...errors, [key]: value});
    }

    // eslint-disable-next-line complexity, sonarjs/cognitive-complexity, max-statements
    const onFinishSuccess = useCallback(async () => {
        if (!validateFields()) {
            const {
                name,
                description,
                isDeleted,
                catalogs,
                templateTimeZone,
                templateSelectionStrategy,
                reviewsWeekdaysIndexes,
                reviewsRatings,
                replyType,
            } = getMapFromObject<AutoReplyToReviewItemType>(form.getFieldsValue(), defaultAutoReplyToReviewData);

            const catalogIds = new Set(catalogs?.map((catalog) => catalog.id || catalog));

            track(
                AnalyticsTarget.ReviewsManagement.AutoReplies.CreateRules,
                catalogsHookResult
                    .filter((catalog: CatalogRulesType) => catalogIds.has(catalog.id))
                    .map((catalog: CatalogRulesType) => getCatalogName(catalog.id))
            );

            const datePeriod = form.getFieldValue('datePeriod');
            const dateStart = form.getFieldValue('dateStart');

            const rawEnabledFrom = infiniteDate ? dateStart : datePeriod && form.getFieldValue('datePeriod')[0];
            const enabledTo = infiniteDate ? null : datePeriod && form.getFieldValue('datePeriod')[1];

            const earliestAllowedDay = dayjs().subtract(AUTO_REPLY_MIN_PAST_OFFSET_DAYS, 'day').startOf('day');
            const earliestAllowedFormatted = earliestAllowedDay.format(AUTO_REPLY_DATE_FORMAT);

            const shouldClampEnabledFrom =
                !temporaryDate &&
                Boolean(rawEnabledFrom) &&
                dayjs(rawEnabledFrom).startOf('day').isBefore(earliestAllowedDay);

            if (shouldClampEnabledFrom) {
                if (infiniteDate) {
                    form.setFieldValue('dateStart', earliestAllowedDay);
                } else {
                    form.setFieldValue('datePeriod', [earliestAllowedDay, form.getFieldValue('datePeriod')[1]]);
                }
            }

            const enabledFrom = shouldClampEnabledFrom ? earliestAllowedDay : rawEnabledFrom;
            const clampedEnabledForDates =
                temporaryDate && enabledForDates.length > 0
                    ? enabledForDates.map((dateString) =>
                          dayjs(dateString).startOf('day').isBefore(earliestAllowedDay)
                              ? earliestAllowedFormatted
                              : dateString
                      )
                    : enabledForDates;

            if (
                temporaryDate &&
                clampedEnabledForDates.some((dateString, index) => dateString !== enabledForDates[index])
            ) {
                setEnabledForDates(clampedEnabledForDates);
            }

            const reviewsTimeBucket = getReviewsTimeBucket();

            const weekdaysIndexes = reviewsWeekdaysIndexes?.[0] === 0 ? [1, 2, 3, 4, 5, 6, 7] : reviewsWeekdaysIndexes;
            const reviewsWithBody = form.getFieldValue('reviewsWithBody');
            const brandCatalogCompany = form.getFieldValue(AutoReplyFormFieldNameEnum.BrandCatalogCompany);
            const selectedStoreCatalogId = form.getFieldValue(AutoReplyFormFieldNameEnum.StoreCatalog);
            const storeCatalogIds = selectedStoreCatalogId ? [selectedStoreCatalogId] : [];

            const conditionsPayload = buildReviewConditionsPayload(
                {
                    attachmentsFilter: form.getFieldValue('reviewsAttachmentsFilter'),
                    keywordRules: form.getFieldValue('reviewsKeywordRules'),
                    autoTagTitles: form.getFieldValue('reviewsAutoTagTitles'),
                },
                aspectsList
            );

            const sharedData = {
                id: autoReplyData.id,
                isDeleted: isDeleted ?? false,
                name,
                description: description || '',
                reviewsWithBody: isShowTemplateBlock
                    ? reviewsWithBody === null
                        ? null
                        : JSON.parse(`${reviewsWithBody}`)
                    : 'true',
                reviewsTimeBucket,
                templateTimeZone: templateTimeZone || '',
                // eslint-disable-next-line no-undefined
                responseTemplates: isShowTemplateBlock ? selectedTemplateList.map(({id}) => id) : undefined,
                templateSelectionStrategy:
                    replyType === AutoReplyTypeEnum.NeuralNetwork ? 'gpt' : templateSelectionStrategy || 'random',
                enabledFrom: !temporaryDate && enabledFrom ? dayjs(enabledFrom).format(AUTO_REPLY_DATE_FORMAT) : null,
                enabledTo: !temporaryDate && enabledTo ? dayjs(enabledTo).format(AUTO_REPLY_DATE_FORMAT) : null,
                enabledForDates: temporaryDate && clampedEnabledForDates.length > 0 ? clampedEnabledForDates : null,
                isTest: form.getFieldValue('is-test'),
                reviewsWeekdaysIndexes: weekdaysIndexes && weekdaysIndexes.length > 0 ? weekdaysIndexes : null,
                ...conditionsPayload,
                mode: temporaryDate ? 'manual' : 'regular',
                enabled: form.getFieldValue('enabled'),
                reviewsRatings: (reviewsRatings || []).filter(Boolean),
            };

            const data: AutoReplyToReviewBodyType = isStoreRuleType
                ? {
                      ...sharedData,
                      brandCatalogCompany,
                      catalogs: storeCatalogIds,
                  }
                : {
                      ...sharedData,
                      catalogs: catalogs || [],
                      uuid: isShowTemplateBlock ? selectorId : selectorIdGpt,
                  };

            setIsSaving(true);

            try {
                await (isCreate
                    ? fetchAutoReplayCreate(data, selectedRuleType)
                    : fetchAutoReplayUpdate(data, autoReplyId ?? '', selectedRuleType));

                snackbar.success(
                    isCreate ? (
                        <Locale stringKey="AUTO_REPLIES__NOTIFICATION__SUCCESS_CREATE" />
                    ) : (
                        <Locale stringKey="AUTO_REPLIES__NOTIFICATION__SUCCESS_UPDATE" />
                    )
                );
                pushUrl(appRoute.autoReplies.path);
            } catch (error) {
                const message = getAutoReplyFormApiErrorMessage(error);

                snackbar.error(
                    message ??
                        (isCreate ? (
                            <Locale stringKey="AUTO_REPLIES__NOTIFICATION__ERROR_CREATE" />
                        ) : (
                            <Locale stringKey="AUTO_REPLIES__NOTIFICATION__ERROR_UPDATE" />
                        ))
                );

                setIsSaving(false);
            }
        } else {
            setIsSaving(false);
            snackbar.error(<Locale stringKey="AUTO_REPLIES__FORM__ERROR" />);
        }
    }, [
        validateFields,
        form,
        catalogsHookResult,
        infiniteDate,
        getReviewsTimeBucket,
        autoReplyData.id,
        isShowTemplateBlock,
        selectedTemplateList,
        temporaryDate,
        enabledForDates,
        selectorId,
        selectorIdGpt,
        getCatalogName,
        isCreate,
        autoReplyId,
        snackbar,
        pushUrl,
        aspectsList,
        isStoreRuleType,
        selectedRuleType,
    ]);

    function onFinishFailed() {
        validateFields();
        snackbar.error(<Locale stringKey="AUTO_REPLIES__FORM__ERROR" />);
    }

    function handleDaysOfWeekChange(days: Array<number>) {
        const lastSelected = days[days.length - 1];

        if (lastSelected === ALL_DAYS_ID) {
            form.setFieldsValue({reviews_weekdays_indexes: [ALL_DAYS_ID]});
        } else {
            form.setFieldsValue({
                reviews_weekdays_indexes: days.filter((day) => day !== ALL_DAYS_ID),
            });
        }
    }

    useEffect(() => {
        if (
            !isStoreRuleType &&
            catalogsHookResult.length > 0 &&
            autoReplyData.catalogs &&
            autoReplyData.catalogs.length > 0
        ) {
            const selectedCatalogs = catalogsHookResult
                .map(({id: fetchedCatalogId}): number => fetchedCatalogId)
                .filter((catalogId: number) => {
                    return autoReplyData.catalogs
                        ?.map(({id: savedCatalogId}): number => savedCatalogId)
                        .includes(catalogId);
                });

            form.setFieldsValue({catalogs: selectedCatalogs});
        }
    }, [autoReplyData, catalogsHookResult, form, isStoreRuleType]);

    useEffect(() => {
        // Wait for the options themselves, not for their labels: the catalog names arrive later and
        // must not re-run this and overwrite what the user has already picked.
        if (isStoreRuleType && autoReplyData.brandCatalogCompany && storeApps.length > 0) {
            const savedCatalogId = autoReplyData.catalogs?.[0]?.id;
            // Leave the field empty when the saved source is gone, rather than showing a value with no option.
            const isSavedCatalogAvailable = storeApps.some((app) => app.catalogId === savedCatalogId);

            form.setFieldsValue({
                [AutoReplyFormFieldNameEnum.BrandCatalogCompany]: autoReplyData.brandCatalogCompany,
                [AutoReplyFormFieldNameEnum.StoreCatalog]: isSavedCatalogAvailable ? savedCatalogId : null,
            });
        }
    }, [autoReplyData, form, isStoreRuleType, storeApps]);

    useEffect(() => {
        if (!isCreate) {
            return;
        }

        if (isStoreRuleType) {
            form.setFieldsValue({
                catalogs: [],
                // eslint-disable-next-line no-undefined
                companies: undefined,
            });
        } else {
            form.setFieldsValue({
                [AutoReplyFormFieldNameEnum.StoreCatalog]: null,
                [AutoReplyFormFieldNameEnum.BrandCatalogCompany]: null,
            });
        }
    }, [form, isCreate, isStoreRuleType]);

    useEffect(() => {
        form.setFieldsValue({
            replyType:
                (autoReplyData.templateSelectionStrategy === 'gpt' && isActiveGptAutoReplyLicence) ||
                !isActiveAutoReplyLicence
                    ? AutoReplyTypeEnum.NeuralNetwork
                    : AutoReplyTypeEnum.Template,
        });
    }, [autoReplyData, form, isActiveAutoReplyLicence, isActiveGptAutoReplyLicence]);

    const isSelectionStrategyDisabled = selectedTemplateList.length < minimumSelectedTemplatesForOrderButtonEnable;

    // eslint-disable-next-line func-style
    const disabledDate: DatePickerProps['disabledDate'] = (current) => {
        const earliestAllowedDay = dayjs().subtract(AUTO_REPLY_MIN_PAST_OFFSET_DAYS, 'day').startOf('day');

        return Boolean(current && current.startOf('day').isBefore(earliestAllowedDay));
    };

    return (
        <Form
            className={classNames(styles.AutoReplyForm, className)}
            form={form}
            layout="vertical"
            name="auto-replay"
            onFinish={onFinishSuccess}
            onFinishFailed={onFinishFailed}
        >
            <PageCard
                bodyClassName={styles.AutoReplyForm_pageCardBody}
                collapsible
                title={<Locale stringKey="AUTO_REPLIES__FORM__BASIC_INFORMATION" />}
            >
                <Form.Item
                    initialValue={autoReplyData.name}
                    label={<Locale stringKey="AUTO_REPLIES__FORM__TITLE" />}
                    name="name"
                    rules={[requiredFieldRule, minLengthFieldRule(2), maxLengthFieldRule(50)]}
                >
                    <Input
                        placeholder={getLocalizedString('AUTO_REPLIES__FORM__ENTER_A_NAME_FOR_THE_RULE')}
                        size="large"
                    />
                </Form.Item>

                <Form.Item
                    initialValue={autoReplyData.description || ''}
                    label={<Locale stringKey="AUTO_REPLIES__FORM__DESCRIPTION" />}
                    name="description"
                    rules={[maxLengthFieldRule(500)]}
                >
                    <TextArea
                        maxLength={500}
                        placeholder={getLocalizedString('AUTO_REPLIES__FORM__ENTER_A_DESCRIPTION')}
                        showCount
                        size="large"
                    />
                </Form.Item>

                <Form.Item label={<Locale stringKey="AUTO_REPLIES__FORM__REPLY_TYPE" />} name="replyType">
                    <Radio.Group className={styles.AutoReplyForm_radioGroup}>
                        <Radio
                            className={styles.AutoReplyForm_radioButton}
                            disabled={!isActiveAutoReplyLicence}
                            value={AutoReplyTypeEnum.Template}
                        >
                            <Locale stringKey="AUTO_REPLIES__FORM__REPLY_TYPE__TEMPLATE" />
                        </Radio>
                        <Radio
                            className={styles.AutoReplyForm_radioButton}
                            disabled={!isActiveGptAutoReplyLicence}
                            value={AutoReplyTypeEnum.NeuralNetwork}
                        >
                            <Locale stringKey="AUTO_REPLIES__FORM__REPLY_TYPE__NEURALLINK" />
                        </Radio>
                    </Radio.Group>
                </Form.Item>
            </PageCard>

            <PageCard
                bodyClassName={styles.AutoReplyForm_pageCardBody}
                collapsible
                title={<Locale stringKey="AUTO_REPLIES__FORM__FILTERS" />}
            >
                <AutoReplyFormReviewTargetSelector
                    initialValue={resolvedRuleType}
                    isDisabled={isEdit}
                    isStoreOptionDisabled={isStoreOptionDisabled}
                />

                {isStoreRuleType ? (
                    <AutoReplyFormFiltersStore
                        catalogNamesIsInProgress={catalogNamesIsInProgress}
                        form={form}
                        getLocalizedString={getLocalizedString}
                        storeApps={storeApps}
                        storeAppsIsInProgress={storeAppsIsInProgress}
                        storeCatalogs={storeCatalogs}
                    />
                ) : (
                    <AutoReplyFormFiltersCompany
                        catalogsHookResult={catalogsHookResult}
                        catalogsIsInProgress={catalogsIsInProgress}
                        companiesSelector={companiesSelector}
                        companiesSelectorGpt={companiesSelectorGpt}
                        getCompaniesMultiselectValidationRule={getCompaniesMultiselectValidationRule}
                        getLocalizedString={getLocalizedString}
                        isShowTemplateBlock={isShowTemplateBlock}
                        selectedCount={selectedCount}
                        selectedCountGpt={selectedCountGpt}
                    />
                )}

                <Form.Item<RatingType>
                    initialValue={autoReplyData.reviewsRatings}
                    label={<Locale stringKey="AUTO_REPLIES__FORM__RATING" />}
                    name="reviewsRatings"
                    rules={[requiredFieldRule]}
                >
                    <RatingMultiSelect disabled={false} />
                </Form.Item>

                {isShowTemplateBlock && (
                    <Form.Item
                        initialValue={
                            autoReplyData.reviewsWithBody === null ? null : String(autoReplyData.reviewsWithBody)
                        }
                        label={<Locale stringKey="AUTO_REPLIES__FORM__CONTENT_OF_REVIEWS" />}
                        name="reviewsWithBody"
                    >
                        <Select<string | null> size="large">
                            <Option key="all-reviews" value={null}>
                                <Locale stringKey="AUTO_REPLIES__FORM__ALL_REVIEWS" />
                            </Option>

                            <Option key="reviews-with-text" value="true">
                                <Locale stringKey="AUTO_REPLIES__FORM__REVIEWS_WITH_TEXT" />
                            </Option>

                            <Option key="reviews-without-text" value="false">
                                <Locale stringKey="AUTO_REPLIES__FORM__REVIEWS_WITHOUT_TEXT" />
                            </Option>
                        </Select>
                    </Form.Item>
                )}

                <Form.Item
                    initialValue={autoReplyData.reviewsAttachmentsFilter ?? null}
                    label={<Locale stringKey="AUTO_REPLIES__FORM__ATTACHMENTS" />}
                    name="reviewsAttachmentsFilter"
                >
                    <Select<string>
                        allowClear
                        placeholder={getLocalizedString('AUTO_REPLIES__FORM__ATTACHMENTS__PLACEHOLDER')}
                        size="large"
                    >
                        <Option key="attachments-with" value={ReviewsAttachmentsFilterEnum.withAttachments}>
                            <Locale stringKey="AUTO_REPLIES__FORM__ATTACHMENTS__WITH" />
                        </Option>

                        <Option key="attachments-without" value={ReviewsAttachmentsFilterEnum.withoutAttachments}>
                            <Locale stringKey="AUTO_REPLIES__FORM__ATTACHMENTS__WITHOUT" />
                        </Option>
                    </Select>
                </Form.Item>

                <Form.Item label={<Locale stringKey="AUTO_REPLIES__FORM__KEYWORDS" />}>
                    <Form.List initialValue={autoReplyData.reviewsKeywordRules ?? []} name="reviewsKeywordRules">
                        {(keywordRules, {add, remove}) => (
                            <div className={styles.AutoReplyForm_keywordRules}>
                                {keywordRules.map(({key, name: fieldName}) => {
                                    const operatorTakenByOtherRow = new Set(
                                        keywordRulesValue
                                            .map((keywordRule) => keywordRule?.operator)
                                            .filter((operator, index) => index !== fieldName && Boolean(operator))
                                    );

                                    return (
                                        <Row align="middle" className={styles.AutoReplyForm_keywordRule} key={key}>
                                            <Form.Item
                                                className={styles.AutoReplyForm_keywordRuleOperator}
                                                initialValue={KeywordOperatorEnum.contains}
                                                name={[fieldName, 'operator']}
                                            >
                                                <Select<string> size="large">
                                                    <Option
                                                        disabled={operatorTakenByOtherRow.has(
                                                            KeywordOperatorEnum.contains
                                                        )}
                                                        value={KeywordOperatorEnum.contains}
                                                    >
                                                        <Locale stringKey="AUTO_REPLIES__FORM__KEYWORDS__CONTAINS" />
                                                    </Option>

                                                    <Option
                                                        disabled={operatorTakenByOtherRow.has(
                                                            KeywordOperatorEnum.notContains
                                                        )}
                                                        value={KeywordOperatorEnum.notContains}
                                                    >
                                                        <Locale stringKey="AUTO_REPLIES__FORM__KEYWORDS__NOT_CONTAINS" />
                                                    </Option>
                                                </Select>
                                            </Form.Item>

                                            <Form.Item
                                                className={styles.AutoReplyForm_keywordRuleWords}
                                                name={[fieldName, 'words']}
                                                rules={[requiredFieldRule]}
                                            >
                                                <Select<Array<string>>
                                                    mode="tags"
                                                    open={false}
                                                    placeholder={getLocalizedString(
                                                        'AUTO_REPLIES__FORM__KEYWORDS__PLACEHOLDER'
                                                    )}
                                                    size="large"
                                                />
                                            </Form.Item>

                                            <Button
                                                aria-label={getLocalizedString('BUTTON__DELETE')}
                                                icon={<FontAwesomeIcon icon={faTrash} />}
                                                onClick={() => remove(fieldName)}
                                                size="large"
                                                type="text"
                                            />
                                        </Row>
                                    );
                                })}

                                {canAddKeywordRule && (
                                    <AddButton
                                        className={styles.AutoReplyForm_keywordRulesAdd}
                                        onClick={() => add({operator: nextKeywordOperator, words: []})}
                                        small
                                    >
                                        <Locale stringKey="AUTO_REPLIES__FORM__KEYWORDS__ADD" />
                                    </AddButton>
                                )}
                            </div>
                        )}
                    </Form.List>
                </Form.Item>

                <Form.Item label={<Locale stringKey="AUTO_REPLIES__FORM__AUTO_TAGS" />} name="reviewsAutoTagTitles">
                    <Select<Array<string>>
                        className={styles.AutoReplyForm_wideInput}
                        data-testid="auto-reply-auto-tags"
                        filterOption={handleFilterOption}
                        loading={aspectsQuery.isLoading}
                        mode="multiple"
                        placeholder={getLocalizedString('AUTO_REPLIES__FORM__AUTO_TAGS__PLACEHOLDER')}
                        size="large"
                    >
                        {aspectsList.map((aspect) => (
                            <Option key={aspect.title} value={aspect.title}>
                                {aspect.title}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    initialValue={
                        autoReplyData.reviewsTimeBucket
                            ? [
                                  dayjs(autoReplyData.reviewsTimeBucket.start, 'HH:mm'),
                                  dayjs(autoReplyData.reviewsTimeBucket.end, 'HH:mm'),
                              ]
                            : [null, null]
                    }
                    label={<Locale stringKey="AUTO_REPLIES__FORM__TIME_TO_RECEIVE_FEEDBACK" />}
                    name="reviewsTimeBucket"
                >
                    <TimeRangePicker
                        className={styles.AutoReplyForm_wideInput}
                        order={false}
                        placeholder={[getLocalizedString('TEXT__TIME_FROM'), getLocalizedString('TEXT__TIME_TO')]}
                        size="large"
                    />
                </Form.Item>

                <Form.Item
                    initialValue={autoReplyData.templateTimeZone || 'Europe/Minsk'}
                    label={<Locale stringKey="AUTO_REPLIES__FORM__TIMEZONE" />}
                    name="templateTimeZone"
                    rules={[requiredFieldRule]}
                >
                    <Select<string>
                        disabled={timezoneLibrary.isInProgress}
                        filterOption={handleFilterOption}
                        loading={timezoneLibrary.isInProgress}
                        placeholder={getLocalizedString('AUTO_REPLIES__FORM__TIMEZONE')}
                        showSearch
                        size="large"
                    >
                        {timezoneList.map((timezoneInList: TimezoneType): JSX.Element => {
                            const {name_for_display: nameForDisplay, name} = timezoneInList;

                            return (
                                <Option key={nameForDisplay} value={name}>
                                    {nameForDisplay}
                                </Option>
                            );
                        })}
                    </Select>
                </Form.Item>
            </PageCard>

            {isShowTemplateBlock && (
                <PageCard
                    bodyClassName={styles.AutoReplyForm_pageCardBody}
                    collapsible
                    title={<Locale stringKey="AUTO_REPLIES__FORM__RESPONSE_TEMPLATES" />}
                >
                    <Form.Item
                        label={
                            <div className={styles.AutoReplyForm_requiredAsterisk}>
                                <Locale stringKey="AUTO_REPLIES__FORM__RESPONSE_TEMPLATES" />
                            </div>
                        }
                    >
                        <div className={styles.AutoReplyForm_buttonCounter}>
                            <Button
                                className={classNames(errors.selectTemplates ? styles.AutoReplyForm_buttonError : '')}
                                onClick={() => setIsSelectTemplatePopupOpen(true)}
                            >
                                <Locale stringKey="AUTO_REPLIES__FORM__SELECT_TEMPLATES" />
                            </Button>

                            <span>
                                <Locale
                                    stringKey="AUTO_REPLIES__FORM__SELECTED"
                                    valueMap={{count: selectedTemplateList.length}}
                                />
                            </span>
                        </div>
                    </Form.Item>

                    <Form.Item
                        initialValue={isShowTemplateBlock ? autoReplyData.templateSelectionStrategy : 'gpt'}
                        label={<Locale stringKey="AUTO_REPLIES__FORM__THE_ORDER_OF_CHOOSING_A_TEMPLATE" />}
                        name="templateSelectionStrategy"
                        rules={isSelectionStrategyDisabled || !isShowTemplateBlock ? [] : [requiredFieldRule]}
                    >
                        <Select<string> disabled={isSelectionStrategyDisabled} filterOption={() => true} size="large">
                            <Option key="order-random" value="random">
                                <Locale stringKey="AUTO_REPLIES__FORM__RANDOM" />
                            </Option>

                            <Option key="order-consistently" value="successively">
                                <Locale stringKey="AUTO_REPLIES__FORM__CONSISTENTLY" />
                            </Option>
                        </Select>
                    </Form.Item>
                </PageCard>
            )}

            <PageCard
                bodyClassName={styles.AutoReplyForm_pageCardBody}
                collapsible
                title={<Locale stringKey="AUTO_REPLIES__FORM__SCHEDULE" />}
            >
                {/* TODO: define all properties */}

                <Checkbox
                    checked={temporaryDate}
                    className={styles.AutoReplyForm_checkbox}
                    onChange={() => {
                        setTemporaryDate(!temporaryDate);
                    }}
                >
                    <Locale stringKey="AUTO_REPLIES__FORM__TEMPORARY" />
                </Checkbox>

                {!temporaryDate && (
                    <>
                        <Form.Item
                            initialValue={
                                autoReplyData.reviewsWeekdaysIndexes &&
                                autoReplyData.reviewsWeekdaysIndexes.length === 7
                                    ? [0]
                                    : autoReplyData.reviewsWeekdaysIndexes
                            }
                            label={<Locale stringKey="AUTO_REPLIES__FORM__DAYS_OF_THE_WEEK" />}
                            name="reviewsWeekdaysIndexes"
                            rules={[requiredFieldRule]}
                        >
                            <Select<Array<number>>
                                mode="multiple"
                                onChange={handleDaysOfWeekChange}
                                placeholder={getLocalizedString('AUTO_REPLIES__FORM__SELECT_THE_DAYS_OF_THE_WEEK')}
                                showSearch={false}
                                size="large"
                            >
                                {selectDayOfWeekContent}
                            </Select>
                        </Form.Item>

                        <Checkbox
                            checked={infiniteDate}
                            className={styles.AutoReplyForm_checkbox}
                            onChange={() => {
                                setInfiniteDate(!infiniteDate);
                            }}
                        >
                            <Locale stringKey="AUTO_REPLIES__FORM__INFINITE_RANGE" />
                        </Checkbox>

                        {infiniteDate ? (
                            <Form.Item
                                help={
                                    errors.datePeriod ? getLocalizedString('VALIDATION__ERROR__FIELD_IS_REQUIRED') : ''
                                }
                                initialValue={autoReplyData.enabledFrom ? dayjs(autoReplyData.enabledFrom) : null}
                                label={<Locale stringKey="AUTO_REPLIES__FORM__DATE_PERIOD" />}
                                name="dateStart"
                                rules={[requiredFieldRule]}
                                validateStatus={errors.dateStart ? 'error' : ''}
                            >
                                <DatePicker
                                    disabledDate={disabledDate}
                                    format={AUTO_REPLY_DATE_FORMAT}
                                    onChange={(value) => {
                                        updateErrors(AutoReplyErrorEnum.datePeriod, !value);
                                    }}
                                    placeholder={getLocalizedString('TEXT__DATE_FROM')}
                                    size="large"
                                />
                            </Form.Item>
                        ) : (
                            <Form.Item
                                help={
                                    errors.datePeriod ? getLocalizedString('VALIDATION__ERROR__FIELD_IS_REQUIRED') : ''
                                }
                                initialValue={[
                                    autoReplyData.enabledFrom ? dayjs(autoReplyData.enabledFrom) : null,
                                    autoReplyData.enabledTo ? dayjs(autoReplyData.enabledTo) : null,
                                ]}
                                label={<Locale stringKey="AUTO_REPLIES__FORM__DATE_PERIOD" />}
                                name="datePeriod"
                                rules={[requiredFieldRule]}
                                validateStatus={errors.datePeriod ? 'error' : ''}
                            >
                                <DatePicker.RangePicker
                                    className={styles.AutoReplyForm_wideInput}
                                    disabledDate={disabledDate}
                                    format={AUTO_REPLY_DATE_FORMAT}
                                    onChange={(value) => {
                                        updateErrors(AutoReplyErrorEnum.datePeriod, !value);
                                    }}
                                    placeholder={[
                                        getLocalizedString('TEXT__DATE_FROM'),
                                        getLocalizedString('TEXT__DATE_TO'),
                                    ]}
                                    size="large"
                                />
                            </Form.Item>
                        )}
                    </>
                )}

                {temporaryDate && (
                    <Form.Item
                        initialValue={enabledForDates.map((dateItem) => dayjs(dateItem))}
                        label={<Locale stringKey="AUTO_REPLIES__FORM__FOR_DATES" />}
                        name="select-dates"
                        rules={[requiredFieldRule]}
                    >
                        <DatePicker
                            disabledDate={disabledDate}
                            format={AUTO_REPLY_DATE_FORMAT}
                            multiple
                            onChange={(date: Dayjs | Array<Dayjs>) => {
                                if (Array.isArray(date)) {
                                    return setEnabledForDates(
                                        date.map((dateItem) => dateItem.format(AUTO_REPLY_DATE_FORMAT))
                                    );
                                }

                                return setEnabledForDates([]);
                            }}
                            value={enabledForDates.map((dateItem) => dayjs(dateItem))}
                        />
                    </Form.Item>
                )}
            </PageCard>

            <PageCard
                bodyClassName={styles.AutoReplyForm_pageCardBody}
                collapsible
                title={<Locale stringKey="AUTO_REPLIES__FORM__CONTROL" />}
            >
                <div className={styles.AutoReplyForm_switch}>
                    <Form.Item initialValue={autoReplyData.enabled} name="enabled" valuePropName="checked">
                        <Switch defaultChecked={autoReplyData.enabled} key={String(autoReplyData.enabled)} />
                    </Form.Item>

                    <Locale stringKey="AUTO_REPLIES__FORM__ENABLE_THE_RULE" />
                </div>

                <Form.Item
                    initialValue={autoReplyData.isTest ? 'True' : 'False'}
                    label={
                        <AdditionalInfo
                            leftNode={<Locale stringKey="AUTO_REPLIES__FORM__METHOD_OF_SENDING__LABEL" />}
                            title={<Locale stringKey="AUTO_REPLIES__FORM__ADDITIONAL_INFO__SELECT_METHOD" />}
                        >
                            <ul style={{paddingLeft: '12px'}}>
                                <li>
                                    <Locale stringKey="AUTO_REPLIES__FORM__ADDITIONAL_INFO__AUTOMATED" />
                                </li>
                                <li>
                                    <Locale stringKey="AUTO_REPLIES__FORM__ADDITIONAL_INFO__MANUAL" />
                                </li>
                            </ul>
                        </AdditionalInfo>
                    }
                    name="is-test"
                    rules={[requiredFieldRule]}
                >
                    <Select
                        options={[
                            {
                                value: 'False',
                                title: getLocalizedString('AUTO_REPLIES__FORM__AUTO_SENDING'),
                                label: getLocalizedString('AUTO_REPLIES__FORM__AUTO_SENDING'),
                            },
                            {
                                value: 'True',
                                title: getLocalizedString('AUTO_REPLIES__FORM__MANUAL_CONFIRMATION'),
                                label: getLocalizedString('AUTO_REPLIES__FORM__MANUAL_CONFIRMATION'),
                            },
                        ]}
                        placeholder={getLocalizedString('AUTO_REPLIES__FORM__METHOD_OF_SENDING__PLACEHOLDER')}
                    />
                </Form.Item>
            </PageCard>

            <div className={styles.AutoReplyForm_buttonList}>
                <Button htmlType="submit" loading={isSaving} type="primary">
                    <Locale stringKey="BUTTON__SAVE" />
                </Button>

                <Row>
                    <Button htmlType="reset">
                        <NavigationLink to={appRoute.autoReplies.path}>
                            <Locale stringKey="BUTTON__RESET_CHANGES" />
                        </NavigationLink>
                    </Button>

                    {isEdit && (
                        <Button danger onClick={handleDelete} type="link">
                            <Locale stringKey="AUTO_REPLIES__FORM__DELETE_RULE" />
                        </Button>
                    )}
                </Row>
            </div>

            <AutoReplyTemplatePopup
                // eslint-disable-next-line no-undefined -- avoid refetching on every catalogs-field edit while the popup is closed
                catalogIds={isSelectTemplatePopupOpen ? selectedCatalogIds : undefined}
                isOpen={isSelectTemplatePopupOpen}
                isPrivateTemplatesDisabled
                onCancel={() => {
                    setIsSelectTemplatePopupOpen(false);
                    setDefaultSelectedTemplateList(defaultSelectedTemplateList);
                }}
                onOk={() => {
                    updateErrors(AutoReplyErrorEnum.selectTemplates, selectedTemplateList.length === 0);
                    setIsSelectTemplatePopupOpen(false);
                }}
                selectedItemList={selectedTemplateList}
                selectedResponseTemplateList={[]}
                setSelectedItemList={setSelectedTemplateList}
                setSelectedResponseTemplateList={noop}
            />
        </Form>
    );
}
